import type { CertificateFormData } from '../../types';
import type { PrescriptionFormData } from '../../hooks/formHooks/usePrescriptionForm';
import { formatDate, formatTime, getPatientPronouns } from '../../helpers';

export function buildCertificateContentBox(formData: CertificateFormData): string {
  const {
    drName, patientName, guardianName, age, gender, identificationMark,
    address, examDate, examTime, diagnosis, noOfDays, fromDate,
    caretakerFullName, caretakerAge, caretakerRelation, certificateType,
    he_she: heSheField, his_her: hisHerField,
  } = formData;

  const guardianPart = guardianName ? `, C/O ${guardianName}` : '';
  const idPart = identificationMark ? `, identification mark - ${identificationMark}` : '';
  const addrPart = address ? `, residing at ${address}` : '';

  const base = `This is to certify that I, ${drName}, have examined ${patientName}${guardianPart}, age ${age} years, ${gender}${idPart}${addrPart} (whose signature is verified below)`;

  const type = (certificateType || '').toUpperCase();

  switch (type) {
    case 'FITNESS CERTIFICATE': {
      const when = examDate ? ` at ${formatTime(examTime)} on ${formatDate(examDate)}` : '';
      return `${base} and have found that the patient is alert and oriented${when}.

• The individual has no history of chronic medical conditions, including asthma or shortness of breath.
• There is no reported history of head injury or seizure episodes.
• The individual has not experienced any flu-like symptoms in the past 15 days.
• Vital signs are within normal physiological limits at the time of examination.
• Gait is normal, with no abnormalities observed.
• Mental status examination reveals no evidence of psychopathology.

I also certify that I have examined the required medical history and have taken them into consideration in arriving at my decision that the patient is currently fit to do regular activities.`;
    }

    case 'SICK LEAVE CERTIFICATE':
      return `${base} and have diagnosed the patient with ${diagnosis}.

Therefore, I do consider that bed rest for ${noOfDays} days starting from ${formatDate(fromDate)} is necessary for the restoration of the patient's health.`;

    case 'MEDICAL CERTIFICATE':
      return `${base} and have diagnosed the patient with ${diagnosis}.`;

    case 'RECOVERY CERTIFICATE': {
      const defaults = getPatientPronouns(gender);
      const he_she = heSheField || defaults.he_she;
      const his_her = hisHerField || defaults.his_her;
      return `${base} and find that ${he_she} has recovered from ${his_her} illness of ${diagnosis} and is now fit to resume duties from ${formatDate(fromDate)}.

I also certify that before arriving at this decision, I have examined the original medical certificate and statement of the case on which leave was granted or extended and have taken these into consideration in arriving at my decision.`;
    }

    case 'UNFIT TO TRAVEL CERTIFICATE':
      return `${base}, and have diagnosed the patient with ${diagnosis}.

Therefore, I do consider that the patient is unfit to travel for ${noOfDays} day(s) from ${formatDate(fromDate)}.`;

    case 'CARETAKER CERTIFICATE':
      return `${base}, and have diagnosed the patient with ${diagnosis}.

${caretakerFullName}, the patient's ${caretakerRelation}, age ${caretakerAge} years, needs to be the constant caretaker of the patient for ${noOfDays} weeks from ${formatDate(fromDate)} for the restoration of the patient's health.`;

    case 'WORK FROM HOME CERTIFICATE':
      return `${base} and has diagnosed the patient with ${diagnosis}.

Therefore, I do consider that work from home for ${noOfDays} days starting from ${formatDate(fromDate)} is necessary, for the restoration of patient's health.`;

    default:
      return `${base} and have diagnosed the patient with ${diagnosis}.`;
  }
}

export function buildCertificateInputs(
  formData: CertificateFormData,
  fieldSchema: string[]
): Record<string, string> {
  console.log('[PDF] buildCertificateInputs — formData:', formData);
  const contentBox = formData.content || buildCertificateContentBox(formData);
  const age = parseInt(formData.age ?? '');
  const isMinor = !isNaN(age) && age < 18;
  const patientOrGuardian = isMinor ? 'Guardian Signature' : "Patient's Signature";

  const allFields: Record<string, string> = {
    drName: formData.drName,
    drNameFooter: formData.drName,
    specialization: formData.specialization,
    regNo: formData.regNo,
    issueDate: formatDate(formData.issueDate),
    docId: formData.docId,
    to: formData.to || '',
    organisation: formData.organisation || '',
    contentBox,
    patientName: formData.patientName,
    certificate_type: formData.certificateType,
    signature: formData.signature || '',
    validity: formData.validity || '',
    fitStamp: formData.fitStamp || '',
    patientOrGuardian,
  };

  const filtered = Object.fromEntries(
    Object.entries(allFields).filter(([key]) => fieldSchema.includes(key))
  );

 
  const missing = fieldSchema.filter(k => !(k in filtered));
  if (missing.length) console.warn('⚠ schema fields not mapped:', missing);
  const loggable = Object.fromEntries(
    Object.entries(filtered).map(([k, v]) =>
      ['signature', 'fitStamp'].includes(k) ? [k, v ? `[base64 ${v.length}chars]` : '(empty)'] : [k, v]
    )
  );
  console.log('values:', loggable);
  console.groupEnd();

  return filtered;
}

function yesNo(toggle: 'Yes' | 'No', details?: string): string {
  if (toggle === 'Yes') return details ? `Yes- ${details}` : 'Yes';
  return 'No';
}

function buildPrescriptionContentBox(rxData: PrescriptionFormData): string {
  const lines: string[] = [];

  if (rxData.complaintsOf) {
    lines.push('Complaints of');
    lines.push(rxData.complaintsOf);
    lines.push('');
  }

  if (rxData.historyOfSymptoms) {
    lines.push(`History of symptoms- ${rxData.historyOfSymptoms}`);
  }

  lines.push(`Past history of similar symptoms- ${yesNo(rxData.pastHistoryOfSimilarSymptoms, rxData.pastHistoryDetails)}`);
  lines.push(`Concurrent illnesses- ${rxData.concurrentIllnesses || 'No'}`);
  lines.push(`Any substance intake- ${yesNo(rxData.substanceIntake, rxData.substanceIntakeDetails)}`);
  lines.push(`Any significant past history of disease- ${rxData.significantPastHistory || 'No'}`);
  lines.push(`Any history of surgery- ${yesNo(rxData.historyOfSurgery, rxData.surgeryDetails)}`);
  lines.push(`History of travel- ${yesNo(rxData.historyOfTravel, rxData.travelDetails)}`);
  lines.push(`Any relevant family history- ${yesNo(rxData.relevantFamilyHistory, rxData.familyHistoryDetails)}`);
  lines.push(`Intensity of symptoms- ${rxData.intensityOfSymptoms}`);
  lines.push(`Self-medication / home remedies- ${yesNo(rxData.selfMedicationHomeRemedies, rxData.selfMedicationDetails)}`);

  if (rxData.specialNotesByRMP) {
    lines.push('');
    lines.push('Special notes by Registered Medical Practitioner-');
    lines.push(`• ${rxData.specialNotesByRMP}`);
  }

  if (rxData.adviceByRMP) {
    lines.push('');
    lines.push('Advice by Registered Medical Practitioner-');
    lines.push(`• ${rxData.adviceByRMP}`);
  }

  if (rxData.fullDiagnosis) {
    lines.push('');
    lines.push(`Full Diagnosis of the illness- ${rxData.fullDiagnosis}`);
  }

  const rxText = rxData.rxLines.filter(Boolean).map((line, i) => `${i + 1}. ${line}`).join('\n');
  if (rxText) {
    lines.push('');
    lines.push('Rx');
    lines.push(rxText);
  }

  return lines.join('\n');
}

export function buildPrescriptionInputs(
  rxData: PrescriptionFormData,
  fieldSchema: string[]
): Record<string, string> {
  const prescriptionContent = buildPrescriptionContentBox(rxData);

  const allFields: Record<string, string> = {
    drName: rxData.drName,
    drNameFooter: rxData.drName,
    specialization: rxData.specialization,
    regNo: rxData.regNo,
    patientName: rxData.patientName,
    isuueDate: formatDate(rxData.issueDate),
    docId: rxData.docId,
    ageGender: rxData.ageGender,
    prescriptionContent,
    signature: rxData.signature || '',
  };

  const filtered = Object.fromEntries(
    Object.entries(allFields).filter(([key]) => fieldSchema.includes(key))
  );

  console.group('[PDF] Prescription inputs');
  console.log('fieldSchema:', fieldSchema);
  console.log('allFields keys:', Object.keys(allFields));
  console.log('filtered keys:', Object.keys(filtered));
  const missing = fieldSchema.filter(k => !(k in filtered));
  if (missing.length) console.warn('⚠ schema fields not mapped:', missing);
  const loggable = Object.fromEntries(
    Object.entries(filtered).map(([k, v]) =>
      k === 'signature' ? [k, v ? `[base64 ${v.length}chars]` : '(empty)'] : [k, v]
    )
  );
  console.log('values:', loggable);
  console.groupEnd();

  return filtered;
}
